/*
 * Copyright (c) 2026 Gabriele Bonora. Tutti i diritti riservati.
 *
 * Software proprietario. Nessuna licenza d'uso, copia, modifica o distribuzione
 * e' concessa in assenza di autorizzazione scritta del titolare dei diritti.
 * Vedere il file LICENSE nella radice del progetto.
 */
import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Square, Pencil } from 'lucide-react';
import { sessioniAPI } from '../api/sessioni';
import { useViewport } from '../hooks/useViewport';
import { fmtDurata } from '../utils/durata';

// Una sola modale per i due interventi dell'admin su una sessione altrui:
//   modo="termina"  chiude un timer ancora aperto
//   modo="modifica" corregge una sessione gia' chiusa
// In entrambi i casi si lavora su data E ora complete, non solo sull'orario: e' quello
// che permette di registrare correttamente una sessione lunga piu' di un giorno.
// Il motivo e' sempre obbligatorio, perche' finisce nella cronologia dell'intervento.
export default function SessioneAdminModal({ sessione, modo, onClose, onDone }) {
  const { isMobile } = useViewport();
  const termina = modo === 'termina';

  const [inizio, setInizio] = useState(perInput(sessione.inizio));
  const [fine, setFine] = useState(perInput(termina ? new Date() : sessione.fine));
  const [pausa, setPausa] = useState(sessione.pausa_minuti || 0);
  const [note, setNote] = useState(sessione.note || '');
  const [motivo, setMotivo] = useState('');
  const [err, setErr] = useState('');

  const inizioDate = new Date(inizio);
  const fineDate = new Date(fine);
  const validi = !isNaN(inizioDate.getTime()) && !isNaN(fineDate.getTime());
  const fineNelFuturo = validi && fineDate.getTime() > Date.now() + 60 * 1000;
  const fineAntecedente = validi && fineDate.getTime() <= inizioDate.getTime();
  const secondiStimati = validi && !fineAntecedente
    ? Math.max(0, Math.round((fineDate - inizioDate) / 1000) - (parseInt(pausa, 10) || 0) * 60)
    : 0;

  const puoConfermare = motivo.trim().length > 0 && validi && !fineNelFuturo && !fineAntecedente;

  const mut = useMutation({
    mutationFn: () => termina
      ? sessioniAPI.termina(sessione.id, {
          fine: fineDate.toISOString(),
          pausa_minuti: parseInt(pausa, 10) || 0,
          motivo: motivo.trim(),
        })
      // Si mandano solo i campi davvero toccati: i campi datetime-local hanno
      // precisione al minuto, quindi rispedirli intatti azzererebbe i secondi e
      // farebbe comparire nella cronologia una modifica che nessuno ha fatto.
      : sessioniAPI.modifica(sessione.id, {
          inizio: inizio !== perInput(sessione.inizio) ? inizioDate.toISOString() : undefined,
          fine: fine !== perInput(sessione.fine) ? fineDate.toISOString() : undefined,
          pausa_minuti: (parseInt(pausa, 10) || 0) !== (sessione.pausa_minuti || 0) ? (parseInt(pausa, 10) || 0) : undefined,
          note: note !== (sessione.note || '') ? note : undefined,
          motivo: motivo.trim(),
        }),
    onSuccess: onDone,
    onError: (e) => setErr(e.response?.data?.error || 'Errore'),
  });

  const nomeTecnico = `${sessione.tecnico_nome || ''} ${sessione.tecnico_cognome || ''}`.trim();

  return (
    <div style={{ ...styles.overlay, ...(isMobile ? styles.overlayMobile : {}) }} onClick={onClose}>
      <div style={{ ...styles.modal, ...(isMobile ? styles.modalMobile : {}) }} onClick={(e) => e.stopPropagation()}>
        <h3 style={styles.titolo}>
          {termina ? <Square size={18} color="#B91C1C" /> : <Pencil size={18} color={ACCENT} />}
          {termina ? `Chiudi il timer di ${nomeTecnico}` : `Correggi la sessione di ${nomeTecnico}`}
        </h3>

        <div style={styles.nota}>
          {termina
            ? 'Le ore verranno conteggiate. La dichiarazione di responsabilità resta vuota: è una autocertificazione del tecnico e non puoi firmarla al posto suo.'
            : 'I valori vecchi e quelli nuovi finiscono nella cronologia dell\'intervento, insieme al tuo nome.'}
        </div>

        <div style={styles.field}>
          <label style={styles.lbl}>Inizio {termina && <span style={styles.hint}>(non modificabile qui)</span>}</label>
          <input
            type="datetime-local"
            value={inizio}
            onChange={(e) => setInizio(e.target.value)}
            disabled={termina}
            style={{ ...styles.input, ...(termina ? styles.inputDisabled : {}) }}
          />
        </div>

        <div style={styles.field}>
          <label style={styles.lbl}>Fine</label>
          <input type="datetime-local" value={fine} onChange={(e) => setFine(e.target.value)} style={styles.input} />
        </div>

        <div style={styles.field}>
          <label style={styles.lbl}>Pausa (minuti)</label>
          <input type="number" min="0" value={pausa} onChange={(e) => setPausa(e.target.value)} style={styles.input} />
        </div>

        {!termina && (
          <div style={styles.field}>
            <label style={styles.lbl}>Note</label>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} style={{ ...styles.input, minHeight: '50px', fontFamily: 'inherit' }} />
          </div>
        )}

        <div style={styles.oreStimate}>Ore conteggiate: <strong>{fmtDurata(secondiStimati)}</strong></div>

        <div style={styles.field}>
          <label style={styles.lbl}>Motivo *</label>
          <input
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            style={styles.input}
            placeholder={termina ? 'es. timer lasciato acceso a fine giornata' : 'es. orari sbagliati, corretti su indicazione del tecnico'}
            maxLength={500}
          />
        </div>

        {fineAntecedente && <div style={styles.errBox}>⚠️ La fine deve essere successiva all'inizio.</div>}
        {fineNelFuturo && <div style={styles.errBox}>⚠️ La fine non può essere nel futuro.</div>}
        {err && <div style={styles.errBox}>{err}</div>}

        <div style={{ ...styles.modalActions, ...(isMobile ? styles.modalActionsMobile : {}) }}>
          <button onClick={onClose} disabled={mut.isPending} style={{ ...styles.cancelBtn, ...(isMobile ? styles.modalBtnMobile : {}) }}>
            Torna indietro
          </button>
          <button
            onClick={() => mut.mutate()}
            disabled={!puoConfermare || mut.isPending}
            style={{
              ...styles.confirmBtn,
              ...(isMobile ? styles.modalBtnMobile : {}),
              backgroundColor: termina ? '#DC2626' : ACCENT,
              opacity: (!puoConfermare || mut.isPending) ? 0.5 : 1,
            }}
          >
            {mut.isPending ? 'Salvo...' : (termina ? 'Chiudi il timer' : 'Salva la correzione')}
          </button>
        </div>
      </div>
    </div>
  );
}

// Date -> "YYYY-MM-DDTHH:MM" in ora locale, il formato che vuole datetime-local
function perInput(valore) {
  const d = new Date(valore);
  if (isNaN(d.getTime())) return '';
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

const ACCENT = '#8B5CF6';

const styles = {
  overlay: { position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: '1rem' },
  overlayMobile: { padding: 0, alignItems: 'flex-end' },
  modal: { backgroundColor: 'white', borderRadius: '14px', padding: '1.5rem', width: '100%', maxWidth: '440px', maxHeight: '90vh', overflow: 'auto' },
  modalMobile: { borderRadius: '16px 16px 0 0', padding: '1.25rem', maxWidth: '100%', maxHeight: '92vh' },
  titolo: { marginTop: 0, marginBottom: '0.85rem', display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '1.08rem' },
  nota: { fontSize: '0.8rem', color: '#6B7280', lineHeight: 1.45, marginBottom: '1rem', padding: '0.7rem', backgroundColor: '#F9FAFB', borderRadius: '8px' },
  field: { display: 'flex', flexDirection: 'column', gap: '0.3rem', marginBottom: '0.85rem' },
  lbl: { fontSize: '0.82rem', fontWeight: 600, color: '#374151' },
  hint: { fontSize: '0.75rem', color: '#9CA3AF', fontWeight: 400 },
  input: { padding: '0.6rem 0.8rem', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '0.95rem', outline: 'none', width: '100%' },
  inputDisabled: { backgroundColor: '#F3F4F6', color: '#6B7280' },
  oreStimate: { padding: '0.7rem', backgroundColor: '#F5F3FF', borderRadius: '8px', textAlign: 'center', fontSize: '0.95rem', marginBottom: '0.85rem' },
  errBox: { backgroundColor: '#FEE2E2', color: '#991B1B', padding: '0.65rem', borderRadius: '8px', fontSize: '0.85rem', marginBottom: '0.85rem' },
  modalActions: { display: 'flex', gap: '0.6rem', justifyContent: 'flex-end' },
  modalActionsMobile: { gap: '0.6rem' },
  modalBtnMobile: { flex: 1, padding: '0.85rem', fontSize: '0.95rem', textAlign: 'center', justifyContent: 'center' },
  cancelBtn: { padding: '0.6rem 1.1rem', backgroundColor: 'white', color: '#374151', border: '1px solid #D1D5DB', borderRadius: '8px', cursor: 'pointer', fontWeight: 500 },
  confirmBtn: { padding: '0.6rem 1.2rem', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600 },
};
